import { defineStore } from "pinia";
import { ref, computed } from "vue";
import useGlobalPlayDataStore from "@/store/globalPlayDataStore.js";
import { reportShare } from "@/api/api/apiStore.js";
import {
    getRecommendSonglist,
    getHomeNewSongs,
    getRecommendGenreSongList,
    getSongGenreList,
    getSongsByGenre,
} from "@/api/api/apiDiscover.js";
import feedListApi from "@/api/api/feedListApi.js";

const useDiscoverDataStore = defineStore("discoverDataStore", () => {
    // State
    const scrollHeight = ref(0);
    const trendingSongList = ref([]);
    const genreSong = ref([]);
    const genreCollection = ref([]);
    const newSongsQueryLastId = ref(0);
    const genreSongsQueryLastId = ref(0);
    const genreDetailScrollHeight = ref(0);
    const trendindActiveIndex = ref(0);

    const newSongsRnderData = ref({
        list: [],
        refreshing: false,
        loadingMore: false,
        isLoading: false,
        loadError: false,
        finished: false,
    });

    const genreSongsRenderData = ref({
        list: [],
        refreshing: false,
        loadingMore: false,
        isLoading: false,
        loadError: false,
        finished: false,
    });

    const formatData = (list) => {
        return list.map((item) => ({
            ...item.song,
            favorite_state: item.is_liked ? 1 : 2,
            uid: item.user.user_id,
            user: item.user,
        }));
    };

    const formatGenreData = () => {
        const list = [];
        genreSong.value.forEach((item) => {
            item.feeds.forEach((citem) => {
                list.push({
                    ...citem.song,
                    favorite_state: citem.is_liked ? 1 : 2,
                    uid: citem.user.user_id,
                    user: citem.user,
                });
            });
        });
        return list;
    };

    // 分享获取shareKey
    const getShareKey = (song_id, song_link) => {
        return new Promise((resolve, reject) => {
            reportShare({
                song_id,
                song_link
            }).then(res => {
                resolve(res)
            })
        })
    }

    // Actions
    const getTrendingSongs = async (fetchFn) => {
        const res = await getRecommendSonglist(fetchFn, {});
        if (res.code == 200) {
            trendingSongList.value = res.data.feeds;
        }
    };

    const getGenreSongList = async (fetchFn) => {
        const res = await getRecommendGenreSongList(fetchFn, {});
        if (res.code == 200) {
            genreSong.value = res.data.genre_recommend_songs;
        }
    };

    const getTotalGenreList = async (fetchFn,) => {
        const res = await getSongGenreList(fetchFn, { priority: 2 });
        if (res.code == 200) {
            genreCollection.value = res.data?.feeds;
        }
    };

    const doLoadMore = async (done) => {
        newSongsRnderData.value.isLoading = true;
        try {
            const res = await getHomeNewSongs(null, {
                page_size: 20,
                last_id: newSongsQueryLastId.value,
            });

            if (res.code == 200) {
                const globalPlayDataStore = useGlobalPlayDataStore();
                newSongsRnderData.value.loadError = false;
                const { data } = res;

                if (!data.feeds) {
                    done(newSongsRnderData.value, true);
                } else {
                    newSongsRnderData.value.list = [
                        ...newSongsRnderData.value.list,
                        ...data.feeds,
                    ];
                    globalPlayDataStore.addPlayDatas(formatData(newSongsRnderData.value.list));
                    newSongsQueryLastId.value = data?.last_id ? data?.last_id : 0;
                    done(newSongsRnderData.value, false);
                }
            } else {
                newSongsRnderData.value.loadError = true;
            }
        } finally {
            newSongsRnderData.value.isLoading = false;
        }
    };

    const doRetry = async () => {
        newSongsRnderData.value.isLoading = true;
        newSongsQueryLastId.value = 0;

        try {
            const res = await getHomeNewSongs(null, {
                page_size: 20,
                last_id: newSongsQueryLastId.value,
            });

            if (res.code == "200") {
                newSongsRnderData.value.loadError = false;
                const { data } = res;

                if (!data.feeds) {
                    newSongsRnderData.value.finished = true;
                    newSongsRnderData.value.list = [];
                } else {
                    newSongsRnderData.value.list = data.feeds;
                    newSongsQueryLastId.value = data?.last_id ? data?.last_id : 0;
                    newSongsRnderData.value.finished = false;
                }
            } else {
                newSongsRnderData.value.loadError = true;
            }
        } finally {
            newSongsRnderData.value.isLoading = false;
        }
    };

    const doGenreRetry = async (fetchFn, query) => {
        genreSongsRenderData.value.isLoading = true;
        try {
            const res = await getSongsByGenre(fetchFn, {
                ...query,
                size: 20,
                last_id: genreSongsQueryLastId.value,
            });

            if (res.code == "200") {
                genreSongsRenderData.value.loadError = false;
                const { data } = res;

                if (!data.feeds) {
                    genreSongsRenderData.value.finished = true;
                    genreSongsRenderData.value.list = [];
                } else {
                    genreSongsRenderData.value.list = data.feeds;
                    genreSongsQueryLastId.value = data?.last_id || 0;
                    genreSongsRenderData.value.finished = false;
                }
            } else {
                genreSongsRenderData.value.loadError = true;
            }
        } finally {
            genreSongsRenderData.value.isLoading = false;
        }
    };

    const doGenreRefresh = async (done, query) => {
        genreSongsRenderData.value.isLoading = true;
        try {
            const res = await getSongsByGenre(null, {
                ...query,
                size: 20,
                // last_id: genreSongsQueryLastId.value,
            });

            if (res.code == 200) {
                const { data } = res;
                if (!data.feeds) {
                    done(genreSongsRenderData.value, true);
                } else {
                    genreSongsRenderData.value.list = data.feeds;
                    genreSongsQueryLastId.value = data?.last_id ? data?.last_id : 0;
                    done(genreSongsRenderData.value, false);
                }
            } else {
                genreSongsRenderData.value.loadError = true;
            }
        } finally {
            genreSongsRenderData.value.isLoading = false;
        }
    };

    const doGenreLoadMore = async (done, query) => {
        genreSongsRenderData.value.isLoading = true;
        try {
            const res = await getSongsByGenre(null, {
                ...query,
                size: 20,
                last_id: genreSongsQueryLastId.value,
            });

            if (res.code == 200) {
                const globalPlayDataStore = useGlobalPlayDataStore();
                genreSongsRenderData.value.loadError = false;
                const { data } = res;

                if (!data.feeds) {
                    done(genreSongsRenderData.value, true);
                } else {
                    genreSongsRenderData.value.list = [
                        ...genreSongsRenderData.value.list,
                        ...data.feeds,
                    ];
                    globalPlayDataStore.addPlayDatas(formatData(genreSongsRenderData.value.list));
                    genreSongsQueryLastId.value = data?.last_id ? data?.last_id : 0;
                    done(genreSongsRenderData.value, false);
                }
            } else {
                genreSongsRenderData.value.loadError = true;
            }
        } finally {
            genreSongsRenderData.value.isLoading = false;
        }
    };

    const updateGrnreLike = (data, favorite_state) => {
        genreSong.value = genreSong.value.map((item) => ({
            ...item,
            feeds: item.feeds.map((feed) =>
                feed.song.song_id === data.song.song_id
                    ? { ...feed, is_liked: !favorite_state }
                    : feed
            ),
        }));
    };

    const updateTrendingCardLike = (data, favorite_state) => {
        trendingSongList.value = trendingSongList.value.map((item) => ({
            ...item,
            is_liked:
                item.song.song_id == data.song.song_id
                    ? !favorite_state
                    : item.is_liked,
        }));
    };

    const updateNewSongCardLike = (data, favorite_state) => {
        const index = newSongsRnderData.value.list.findIndex(
            (itemF) => itemF.song.song_id === data.song.song_id
        );
        if (index > -1) {
            const item = newSongsRnderData.value.list[index];
            newSongsRnderData.value.list[index] = {
                ...item,
                is_liked: !favorite_state,
            };
            newSongsRnderData.value.list = [...newSongsRnderData.value.list];
        }
    };

    const updateGenreSongsLike = (data, favorite_state) => {
        const index = genreSongsRenderData.value.list.findIndex(
            (itemF) => itemF.song.song_id === data.song.song_id
        );
        if (index > -1) {
            genreSongsRenderData.value.list[index] = {
                ...genreSongsRenderData.value.list[index],
                is_liked: !favorite_state,
            };
        }
    };

    const updateDiscoverScrollTop = (height) => {
        scrollHeight.value = height;
    };

    const updateGenreScrollTop = (height) => {
        genreDetailScrollHeight.value = height;
    };

    const addTrendingPlayList = () => {
        const globalPlayDataStore = useGlobalPlayDataStore();
        globalPlayDataStore.setCurPlayGroup("trendingList");
        globalPlayDataStore.playlist = [];
        globalPlayDataStore.addPlayDatas(formatData(trendingSongList.value));
    };

    const toggleTrendingPlay = (item) => {
        const globalPlayDataStore = useGlobalPlayDataStore();
        globalPlayDataStore.setCurPlayGroup("trendingList");
        globalPlayDataStore.setPlayDatas(formatData(trendingSongList.value), {
            ...item.song,
            is_liked: item.is_liked ? 1 : 2,
            favorite_state: item.is_liked ? 1 : 2,
            uid: item.user.user_id,
            user: item.user,
        });
    };

    const addGenrePlayList = () => {
        const globalPlayDataStore = useGlobalPlayDataStore();
        globalPlayDataStore.setCurPlayGroup("genreList");
        globalPlayDataStore.playlist = [];
        globalPlayDataStore.addPlayDatas(formatGenreData());
    };

    const toggleGenrePlay = (item) => {
        const globalPlayDataStore = useGlobalPlayDataStore();
        globalPlayDataStore.setCurPlayGroup("genreList");
        globalPlayDataStore.setPlayDatas(formatGenreData(), {
            ...item.song,
            is_liked: item.is_liked ? 1 : 2,
            favorite_state: item.is_liked ? 1 : 2,
            uid: item.user.user_id,
            user: item.user,
        });
    };

    const addNewSongsPlayList = () => {
        const globalPlayDataStore = useGlobalPlayDataStore();
        globalPlayDataStore.setCurPlayGroup("newSongList");
        globalPlayDataStore.playlist = [];
        globalPlayDataStore.addPlayDatas(formatData(newSongsRnderData.value.list));
    };

    const toggleNewSongsPlay = (item) => {
        const globalPlayDataStore = useGlobalPlayDataStore();
        globalPlayDataStore.setCurPlayGroup("newSongList");
        globalPlayDataStore.setPlayDatas(formatData(newSongsRnderData.value.list), {
            ...item.song,
            is_liked: item.is_liked ? 1 : 2,
            favorite_state: item.is_liked ? 1 : 2,
            uid: item.user.user_id,
            user: item.user,
        });
    };

    const toggleGenreDetailPlay = (item) => {
        const globalPlayDataStore = useGlobalPlayDataStore();
        globalPlayDataStore.setCurPlayGroup("genreTotalList");
        globalPlayDataStore.setPlayDatas(formatData(genreSongsRenderData.value.list), {
            ...item.song,
            is_liked: item.is_liked ? 1 : 2,
            favorite_state: item.is_liked ? 1 : 2,
            uid: item.user.user_id,
            user: item.user,
        });
    };

    const updateGlobalPlayFavorite = (song_id, favorite_state) => {
        // Update trending list
        const trendingIndex = trendingSongList.value.findIndex(
            (item) => item.song.song_id === song_id
        );
        if (trendingIndex > -1) {
            trendingSongList.value[trendingIndex] = {
                ...trendingSongList.value[trendingIndex],
                is_liked: favorite_state == 1 
            };
        }

        // Update genre song list
        genreSong.value = genreSong.value.map((item) => ({
            ...item,
            feeds: item.feeds.map((feed) =>
                feed.song.song_id === song_id
                    ? { ...feed, is_liked: favorite_state == 1 }
                    : feed
            ),
        }));

        // Update new songs list
        const newSongsIndex = newSongsRnderData.value.list.findIndex(
            (itemF) => itemF.song.song_id === song_id
        );
        if (newSongsIndex > -1) {
            const item = newSongsRnderData.value.list[newSongsIndex];
            newSongsRnderData.value.list[newSongsIndex] = {
                ...item,
                is_liked: favorite_state == 1 
            };
            newSongsRnderData.value.list = [...newSongsRnderData.value.list];
        }

        // Update genre songs list
        const genreSongsIndex = genreSongsRenderData.value.list.findIndex(
            (itemF) => itemF.song.song_id === song_id
        );
        if (genreSongsIndex > -1) {
            const item = genreSongsRenderData.value.list[genreSongsIndex];
            genreSongsRenderData.value.list[genreSongsIndex] = {
                ...item,
                is_liked: favorite_state == 1 
            };
            genreSongsRenderData.value.list = [...genreSongsRenderData.value.list];
        }
    };

    const doPlayStateReport = (item, play_type) => {
        return new Promise((resolve, reject) => {
            feedListApi
                .doPlayStateReport({
                    song_id: item.song.song_id,
                    play_type: play_type,
                })
                .then((res) => {
                    if (res.code == 200) {
                        resolve();
                    } else {
                        reject(res?.msg ?? "save failed");
                    }
                })
                .catch((e) => {
                    reject(e);
                });
        });
    };

    const clearGenreListTotal = () => {
        // genreSongsTotal.value = 0;
        genreSongsRenderData.value = {
            list: [],
            refreshing: false,
            loadingMore: false,
            isLoading: false,
            loadError: false,
            finished: false,
        };
    };

    const updateTrendingActiveIndex = (index) => {
        trendindActiveIndex.value = index;
    };

    const initPreviewList = () => {
        sessionStorage.setItem("discover_reported_list", JSON.stringify([]));
        sessionStorage.setItem("discover_show_list", JSON.stringify([]));
    };

    const addShowItem = (song) => {
        const song_id = song.song_id;
        let discover_show_list =
            JSON.parse(sessionStorage.getItem("discover_show_list")) || [];

        if (!discover_show_list.some((item) => item.song_id === song_id)) {
            discover_show_list.push({
                song_id: song_id,
                control_types: song.control_types,
            });
            sessionStorage.setItem(
                "discover_show_list",
                JSON.stringify(discover_show_list)
            );
        }
    };

    const reportDiscoverList = () => {
        let discover_reported_list =
            JSON.parse(sessionStorage.getItem("discover_reported_list")) || [];
        let discover_show_list =
            JSON.parse(sessionStorage.getItem("discover_show_list")) || [];

        if (!discover_show_list.length) {
            return [];
        }

        // Calculate unreported items
        const reported_ids = new Set(
            discover_reported_list.map((item) => item.song_id)
        );
        const new_to_report = discover_show_list.filter(
            (item) => !reported_ids.has(item.song_id)
        );

        // Update reported list
        discover_reported_list = [...discover_reported_list, ...new_to_report];
        sessionStorage.setItem(
            "discover_reported_list",
            JSON.stringify(discover_reported_list)
        );

        return {
            ids: new_to_report.map((item) => item.song_id),
            control_features_ids: new_to_report
                .map((item) => ({
                    song_id: item.song_id,
                    control_types: item.control_types?.filter((it) => it != "5") || null,
                }))
                .filter((it) => it.control_types?.length)
                .map((item) => item.song_id),
        };
    };

    const clearShowList = () => {
        sessionStorage.setItem("discover_show_list", JSON.stringify([]));
    };

    const setGenreSongsQueryLastId = (num) => {
        genreSongsQueryLastId.value = num;
    };

    const removeSongItem = (song) => {
        // Remove from new songs list
        const newSongsIndex = newSongsRnderData.value.list.findIndex(
            (item) => item.song?.song_id == song.song_id
        );
        if (newSongsIndex > -1) {
            newSongsRnderData.value.list.splice(newSongsIndex, 1);
            newSongsRnderData.value.list = [...newSongsRnderData.value.list];
        }

        // Remove from trending list
        const trendingIndex = trendingSongList.value.findIndex(
            (item) => item.song?.song_id == song.song_id
        );
        if (trendingIndex > -1) {
            trendingSongList.value.splice(trendingIndex, 1);
            trendingSongList.value = [...trendingSongList.value];
        }

        // Remove from genre list
        genreSong.value = genreSong.value.filter((item) => {
            const feedIndex = item.feeds.findIndex(
                (feed) => feed.song?.song_id == song.song_id
            );
            if (feedIndex > -1) {
                item.feeds.splice(feedIndex, 1);
                return item.feeds.length > 0;
            }
            return true;
        });
        genreSong.value = [...genreSong.value];
    };

    return {
        // State
        scrollHeight,
        trendingSongList,
        genreSong,
        genreCollection,
        newSongsRnderData,
        newSongsQueryLastId,
        genreSongsRenderData,
        genreSongsQueryLastId,
        genreDetailScrollHeight,
        trendindActiveIndex,

        // Actions
        getTrendingSongs,
        getGenreSongList,
        getTotalGenreList,
        doLoadMore,
        doRetry,
        doGenreRetry,
        doGenreRefresh,
        doGenreLoadMore,
        updateGrnreLike,
        updateTrendingCardLike,
        updateNewSongCardLike,
        updateGenreSongsLike,
        updateDiscoverScrollTop,
        updateGenreScrollTop,
        addTrendingPlayList,
        toggleTrendingPlay,
        addGenrePlayList,
        toggleGenrePlay,
        addNewSongsPlayList,
        toggleNewSongsPlay,
        toggleGenreDetailPlay,
        updateGlobalPlayFavorite,
        doPlayStateReport,
        clearGenreListTotal,
        updateTrendingActiveIndex,
        initPreviewList,
        addShowItem,
        reportDiscoverList,
        clearShowList,
        setGenreSongsQueryLastId,
        removeSongItem,
        getShareKey,
    };
});

export default useDiscoverDataStore
